/**
 * Sticky Add-to-Cart bar.
 *
 * Reveals itself once the main Add-to-Cart button in `.product-details` has scrolled
 * above the viewport, and proxies its own button's click to that real button so all
 * existing cart / variant / drawer behaviour is reused (no duplicated cart logic).
 */
class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    // The bar is authored inside .product-details, which is `position: sticky` with its own
    // stacking context — that traps a `position: fixed` child so later sections (e.g. the
    // recommended-products carousel) paint over it. Teleporting to <body> lets it stack above
    // page content. Skipped in the theme editor so block selection/rendering keeps working.
    if (!window.Shopify?.designMode && this.parentElement !== document.body) {
      // Moving the node re-triggers connectedCallback (now parented to <body>), which runs the
      // real setup below — so return here to avoid initialising twice.
      document.body.appendChild(this);
      return;
    }

    this.mainButton = document.querySelector('.product-details .add-to-cart-button');
    this.observeTarget =
      document.querySelector('.product-details .product-form-buttons') ?? this.mainButton;

    if (!this.observeTarget) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Only reveal once the buy area has scrolled ABOVE the viewport (not when it's
        // still below the fold on a short page).
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggleAttribute('hidden', !scrolledPast);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.observeTarget);

    this.proxyButton = this.querySelector('[data-sticky-add]');
    if (this.proxyButton) {
      this.boundClick = this.handleClick.bind(this);
      this.proxyButton.addEventListener('click', this.boundClick);
    }
  }

  handleClick() {
    // Re-query in case the section re-rendered on a variant change.
    const target = document.querySelector('.product-details .add-to-cart-button');
    if (target instanceof HTMLElement) target.click();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.proxyButton && this.boundClick) {
      this.proxyButton.removeEventListener('click', this.boundClick);
    }
  }
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}
